import { TranslocoService } from '@ngneat/transloco';
import { TransactionDto } from 'src/app/api-client';
import { avatarInitialsFromEmail } from 'src/app/shared/helpers/avatar';
import { croatianPluralBucket } from 'src/app/shared/helpers/croatian-plural';

/**
 * Shared presentation helpers for the transactions list and the
 * transaction-detail page (Console v2).
 */

// Avatar helpers live in shared (used by members/staff lists too) — re-exported
// here so transaction consumers keep a single import site.
export { avatarColor } from 'src/app/shared/helpers/avatar';

/** Derive up to two initials from the email local part (no name field exists). */
export function avatarInitials(email: string | undefined | null): string {
  return avatarInitialsFromEmail(email);
}

/**
 * The DTO has no `type` field, so the kind is derived from the fields present:
 *  - coupon present + points < 0   → coupon bought (customer spent points)
 *  - coupon present + points >= 0   → coupon rewarded / gifted (no points spent)
 *  - no coupon + has receipt (invoiceNo/jir) → invoice scan (points earned)
 *  - no coupon + no receipt          → manual point adjustment by an operator
 */
export type TransactionKind =
  | 'INVOICE_SCAN'
  | 'MANUAL'
  | 'COUPON_BOUGHT'
  | 'COUPON_REWARDED';

export function transactionKind(
  transaction: TransactionDto | null | undefined
): TransactionKind {
  const coupon = (transaction?.couponName ?? '').trim();
  if (coupon) {
    return (transaction?.points ?? 0) < 0 ? 'COUPON_BOUGHT' : 'COUPON_REWARDED';
  }
  // Prefer the backend discriminator (added for audit F-03; read defensively until the
  // generated client includes it): a failed scan has no invoiceNo/jir and would otherwise
  // be misread as a manual points change.
  const rawType = (transaction as { type?: string })?.type;
  if (rawType === 'INVOICE_SCAN') return 'INVOICE_SCAN';
  if (rawType === 'MANUAL_TRANSACTION') return 'MANUAL';
  const hasReceipt = !!(transaction?.invoiceNo || transaction?.jir);
  return hasReceipt ? 'INVOICE_SCAN' : 'MANUAL';
}

/** True for coupon transactions (bought or rewarded) — these carry the coupon
 *  image in `transaction.image`, so the kind cell can show it instead of an icon. */
export function isCouponKind(
  transaction: TransactionDto | null | undefined
): boolean {
  const kind = transactionKind(transaction);
  return kind === 'COUPON_BOUGHT' || kind === 'COUPON_REWARDED';
}

/** Material icon representing each transaction kind. */
export function transactionKindIcon(kind: TransactionKind): string {
  switch (kind) {
    case 'INVOICE_SCAN':
      return 'receipt_long';
    case 'MANUAL':
      return 'tune';
    case 'COUPON_BOUGHT':
      return 'local_offer';
    case 'COUPON_REWARDED':
      return 'card_giftcard';
  }
}

/** Status → v2 chip modifier class. */
export function statusChipClass(status: TransactionDto.StatusEnum): string {
  switch (status) {
    case 'VALIDATION_PENDING':
      return 'chip--warn';
    case 'SUCCESS':
      return 'chip--success';
    case 'DECLINED':
      return 'chip--accent';
    case 'ERROR':
      return 'chip--danger';
    default:
      return 'chip--neutral';
  }
}

/**
 * Relative "danas/jučer · HH:mm" label (falls back to dd.MM.yyyy. for
 * older dates). Day words are translated through Transloco.
 */
export function relativeDateTime(
  dateString: string | undefined | null,
  transloco: TranslocoService
): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round(
    (startOfToday.getTime() - startOfDate.getTime()) / 86400000
  );

  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  let day: string;
  if (dayDiff === 0) {
    day = transloco.translate('transactions.today');
  } else if (dayDiff === 1) {
    day = transloco.translate('transactions.yesterday');
  } else {
    day = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}.`;
  }
  return `${day} · ${time}`;
}

function pad(num: number): string {
  return num < 10 ? `0${num}` : `${num}`;
}

/** Points-per-currency-unit ratio of the row's loyalty program, when known. */
export function priceToPointsRatio(
  transaction: TransactionDto | undefined | null
): number | null {
  const ratio = transaction?.loyalty?.loyaltyProgram?.priceToPointsRatio;
  return typeof ratio === 'number' && ratio > 0 ? ratio : null;
}

/** Receipt amount derived as points ÷ ratio; null when the ratio is unknown. */
export function transactionAmount(
  transaction: TransactionDto | undefined | null
): number | null {
  const ratio = priceToPointsRatio(transaction);
  if (ratio === null || typeof transaction?.points !== 'number') return null;
  return transaction.points / ratio;
}

/** Currency symbol of the row's loyalty program (defaults to €). */
export function currencySymbol(
  transaction: TransactionDto | undefined | null
): string {
  const currency = transaction?.loyalty?.loyaltyProgram?.currency;
  if (!currency || currency.toUpperCase() === 'EUR') return '€';
  return currency;
}

/** Formatted amount, e.g. "€24,80"; null when it cannot be derived. */
export function formatAmount(
  transaction: TransactionDto | undefined | null
): string | null {
  const amount = transactionAmount(transaction);
  if (amount === null) return null;
  const formatted = amount.toLocaleString('hr-HR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currencySymbol(transaction)}${formatted}`;
}

/**
 * The `jir` field holds either a JIR (UUID format) or a ZKI (32-char hex).
 * Detect which one it is from the value format; null when ambiguous.
 */
export function jirKind(value: string | undefined | null): 'JIR' | 'ZKI' | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)
  ) {
    return 'JIR';
  }
  if (/^[0-9a-f]{32}$/i.test(trimmed)) {
    return 'ZKI';
  }
  return null;
}

/** `loyaltyEnded` is not (yet) part of the generated model — read defensively. */
export function isLoyaltyEnded(
  transaction: TransactionDto | undefined | null
): boolean {
  return !!(transaction as { loyaltyEnded?: boolean })?.loyaltyEnded;
}

/** Croatian plural bucket (one / few / other) for a cardinal count. */
export function croatianPluralSuffix(count: number): 'One' | 'Few' | 'Other' {
  const bucket = croatianPluralBucket(count);
  return bucket === 'one' ? 'One' : bucket === 'few' ? 'Few' : 'Other';
}
